import { useEffect, useMemo } from 'react';

import { useIntl } from 'react-intl';

import { AnimatedTypingCard } from './components/animated-typing-card';
import { JiraIssue } from './constants/types';
import { AnimationSteps } from './constants/ui';
import { JswAnimatedBoard } from './jsw-animated-board';
import { AnimatedJiraBoardContainer, useAnimatedJiraBoardStore } from './state';

export type AnimatedJiraTourProps = {
	displayMode?: 'animated' | 'static';
	layoutMode?: 'legacy' | 'fixed-scene';
	animatedJiraCardOverride?: JiraIssue;
	inProgressCardsOverride?: Array<JiraIssue>;
	doneCardsOverride?: Array<JiraIssue>;
};

const AnimatedJiraTourContent = (props: AnimatedJiraTourProps) => {
	const [{ animationStep }, { initializeTour }] = useAnimatedJiraBoardStore();
	const isCardTyping = useMemo(() => {
		return animationStep === AnimationSteps.CARD_TYPING;
	}, [animationStep]);

	const intl = useIntl();

	const messages = useMemo(
		() => ({
			toDo: intl.formatMessage({
				id: 'animated-jira-tour.constants.messages.toDo',
				defaultMessage: 'To Do',
			}),
			inProgress: intl.formatMessage({
				id: 'animated-jira-tour.constants.messages.inProgress',
				defaultMessage: 'In Progress',
			}),
			done: intl.formatMessage({
				id: 'animated-jira-tour.constants.messages.done',
				defaultMessage: 'Done',
			}),
			todoStaticLabel: intl.formatMessage({
				id: 'animated-jira-tour.constants.messages.todoStaticLabel',
				defaultMessage: 'Design AI shopping suggestions for homepage',
			}),
			inProgStaticLabel1: intl.formatMessage({
				id: 'animated-jira-tour.constants.messages.inProgStaticLabel1',
				defaultMessage: 'Create AI-generated shopping suggestions for homepage',
			}),
			inProgStaticLabel2: intl.formatMessage({
				id: 'animated-jira-tour.constants.messages.inProgStaticLabel2',
				defaultMessage: 'Add advanced analytics tracking events',
			}),
			doneStaticLabel1: intl.formatMessage({
				id: 'animated-jira-tour.constants.messages.doneStaticLabel1',
				defaultMessage: 'Improve payment checkout time on mobile',
			}),
			doneStaticLabel2: intl.formatMessage({
				id: 'animated-jira-tour.constants.messages.doneStaticLabel2',
				defaultMessage: 'Define requirements to use new AI integrations',
			}),
			animatedCardHeading: intl.formatMessage({
				id: 'animated-jira-tour.constants.messages.animatedCardHeading',
				defaultMessage: 'Design new billing API',
			}),
			createIssueModalIssueTypeAssetLabel1: intl.formatMessage({
				id: 'animated-jira-tour.constants.messages.createIssueModalIssueTypeAssetLabel1',
				defaultMessage: 'Issue type: Task',
			}),
			createIssueModalIssueTypeAssetLabel2: intl.formatMessage({
				id: 'animated-jira-tour.constants.messages.createIssueModalIssueTypeAssetLabel2',
				defaultMessage: 'Issue type: Story',
			}),
			createIssueModalIssueTypeAssetLabel3: intl.formatMessage({
				id: 'animated-jira-tour.constants.messages.createIssueModalIssueTypeAssetLabel3',
				defaultMessage: 'Issue type: Bug',
			}),
			createIssueModalPriorityAssetLabel1: intl.formatMessage({
				id: 'animated-jira-tour.constants.messages.createIssueModalPriorityAssetLabel1',
				defaultMessage: 'Priority: High',
			}),
			createIssueModalPriorityAssetLabel2: intl.formatMessage({
				id: 'animated-jira-tour.constants.messages.createIssueModalPriorityAssetLabel2',
				defaultMessage: 'Priority: Critical',
			}),
			createIssueModalPriorityAssetLabel3: intl.formatMessage({
				id: 'animated-jira-tour.constants.messages.createIssueModalPriorityAssetLabel3',
				defaultMessage: 'Priority: Blocker',
			}),
			createIssueModalPriorityAssetLabel4: intl.formatMessage({
				id: 'animated-jira-tour.constants.messages.createIssueModalPriorityAssetLabel4',
				defaultMessage: 'Priority: Minor',
			}),
			createIssueModalAssigneeAssetLabel1: intl.formatMessage({
				id: 'animated-jira-tour.constants.messages.createIssueModalAssigneeAssetLabel1',
				defaultMessage: 'Assignee: Alvin',
			}),
			createIssueModalAssigneeAssetLabel2: intl.formatMessage({
				id: 'animated-jira-tour.constants.messages.createIssueModalAssigneeAssetLabel2',
				defaultMessage: 'Assignee: Jane',
			}),
			createIssueModalAssigneeAssetLabel3: intl.formatMessage({
				id: 'animated-jira-tour.constants.messages.createIssueModalAssigneeAssetLabel3',
				defaultMessage: 'Assignee: Blake',
			}),
			epicNameFeatures: intl.formatMessage({
				id: 'animated-jira-tour.constants.messages.epicNameFeatures',
				defaultMessage: 'features',
			}),
			epicNameAnalytics: intl.formatMessage({
				id: 'animated-jira-tour.constants.messages.epicNameAnalytics',
				defaultMessage: 'analytics',
			}),
			epicNamePayments: intl.formatMessage({
				id: 'animated-jira-tour.constants.messages.epicNamePayments',
				defaultMessage: 'payments',
			}),
			priorityHigh: intl.formatMessage({
				id: 'animated-jira-tour.constants.messages.priorityHigh',
				defaultMessage: 'high',
			}),
			priorityCritical: intl.formatMessage({
				id: 'animated-jira-tour.constants.messages.priorityCritical',
				defaultMessage: 'critical',
			}),
			priorityMinor: intl.formatMessage({
				id: 'animated-jira-tour.constants.messages.priorityMinor',
				defaultMessage: 'minor',
			}),
			priorityBlocker: intl.formatMessage({
				id: 'animated-jira-tour.constants.messages.priorityBlocker',
				defaultMessage: 'blocker',
			}),
			issueTypeBug: intl.formatMessage({
				id: 'animated-jira-tour.constants.messages.issueTypeBug',
				defaultMessage: 'bug',
			}),
			issueTypeStory: intl.formatMessage({
				id: 'animated-jira-tour.constants.messages.issueTypeStory',
				defaultMessage: 'story',
			}),
			issueTypeTask: intl.formatMessage({
				id: 'animated-jira-tour.constants.messages.issueTypeTask',
				defaultMessage: 'task',
			}),
			epicNameAssets: intl.formatMessage({
				id: 'animated-jira-tour.constants.messages.epicNameAssets',
				defaultMessage: 'assets',
			}),
			epicNameStrategy: intl.formatMessage({
				id: 'animated-jira-tour.constants.messages.epicNameStrategy',
				defaultMessage: 'strategy',
			}),
			epicNameBudget: intl.formatMessage({
				id: 'animated-jira-tour.constants.messages.epicNameBudget',
				defaultMessage: 'budget',
			}),
			epicNameQ1Campaign: intl.formatMessage({
				id: 'animated-jira-tour.constants.messages.epicQ1Campaign',
				defaultMessage: 'q1 campaign',
			}),
		}),
		[intl],
	);

	useEffect(() => {
		void initializeTour(messages);
	}, [messages, initializeTour]);

	return (
		<>
			{props.displayMode !== 'static' && isCardTyping && (
				<AnimatedTypingCard card={props.animatedJiraCardOverride} />
			)}
			{(props.displayMode === 'static' || !isCardTyping) && (
				<JswAnimatedBoard
					displayMode={props.displayMode}
					layoutMode={props.layoutMode}
					animatedJiraCardOverride={props.animatedJiraCardOverride}
					inProgressCardsOverride={props.inProgressCardsOverride}
					doneCardsOverride={props.doneCardsOverride}
				/>
			)}
		</>
	);
};

export const AnimatedJiraTour = (props: AnimatedJiraTourProps) =>
	props.layoutMode === 'fixed-scene' ? (
		<AnimatedJiraBoardContainer key={props.displayMode ?? 'animated'} isGlobal={false}>
			<AnimatedJiraTourContent {...props} />
		</AnimatedJiraBoardContainer>
	) : (
		<AnimatedJiraTourContent {...props} />
	);
